interface AuthConfig {
  enabled: boolean;
  domain?: string;
  clientId?: string;
  redirectUri?: string;
}

const TOKEN_KEY = 'thisbot.auth.tokens';
const VERIFIER_KEY = 'thisbot.auth.pkce';

function base64Url(bytes: ArrayBuffer): string {
  const data = String.fromCharCode(...new Uint8Array(bytes));
  return btoa(data).replace(/\+/g, '-').replace(/\//g, '_').replace(/=+$/, '');
}

function randomVerifier(): string {
  const bytes = crypto.getRandomValues(new Uint8Array(48));
  return base64Url(bytes.buffer);
}

async function loadConfig(): Promise<AuthConfig> {
  const response = await fetch('/api/auth/config');
  if (!response.ok) throw new Error('Authentication is not configured yet.');
  return response.json() as Promise<AuthConfig>;
}

export function accessToken(): string | undefined {
  try {
    const raw = sessionStorage.getItem(TOKEN_KEY);
    if (!raw) return undefined;
    const value = JSON.parse(raw) as { access_token?: string; expires_at?: number };
    if (!value.access_token || !value.expires_at || value.expires_at < Date.now()) return undefined;
    return value.access_token;
  } catch {
    return undefined;
  }
}

export async function startLogin(): Promise<void> {
  const config = await loadConfig();
  if (!config.enabled || !config.domain || !config.clientId || !config.redirectUri) {
    window.location.assign('/app?demo=1');
    return;
  }

  const verifier = randomVerifier();
  const challenge = base64Url(await crypto.subtle.digest('SHA-256', new TextEncoder().encode(verifier)));
  sessionStorage.setItem(VERIFIER_KEY, verifier);
  const params = new URLSearchParams({
    client_id: config.clientId,
    response_type: 'code',
    scope: 'openid email profile',
    redirect_uri: config.redirectUri,
    code_challenge_method: 'S256',
    code_challenge: challenge,
  });
  window.location.assign(`${config.domain}/oauth2/authorize?${params.toString()}`);
}

export async function finishLogin(): Promise<void> {
  const query = new URLSearchParams(window.location.search);
  const code = query.get('code');
  const verifier = sessionStorage.getItem(VERIFIER_KEY);
  const config = await loadConfig();
  if (!code || !verifier || !config.domain || !config.clientId || !config.redirectUri) {
    throw new Error('The sign-in response is incomplete. Please start again.');
  }

  const body = new URLSearchParams({
    grant_type: 'authorization_code',
    client_id: config.clientId,
    redirect_uri: config.redirectUri,
    code,
    code_verifier: verifier,
  });
  const response = await fetch(`${config.domain}/oauth2/token`, {
    method: 'POST',
    headers: { 'content-type': 'application/x-www-form-urlencoded' },
    body,
  });
  if (!response.ok) throw new Error('Sign-in could not be completed.');
  const tokens = (await response.json()) as { access_token: string; expires_in: number };
  sessionStorage.setItem(
    TOKEN_KEY,
    JSON.stringify({ ...tokens, expires_at: Date.now() + tokens.expires_in * 1000 - 30_000 }),
  );
  sessionStorage.removeItem(VERIFIER_KEY);
  window.location.replace('/app');
}

export function signOut(): void {
  sessionStorage.removeItem(TOKEN_KEY);
  window.location.assign('/');
}

